import { css } from '@emotion/css';
import {
  h5Css,
  m,
  mobileMediaQuery,
  nonMobileMediaQuery,
  smallMobileMediaQuery,
} from '@snapchat/snap-design-system-marketing';

/* Styles copied from: marketing-web/packages/web/src/index.ejs */
const normalizeCss = css`
  font-family: ${m('--font-family')};

  * {
    box-sizing: border-box;
    margin: 0;
  }
`;

const cookieTitleFontRampCss = css`
  ${h5Css}
  margin-bottom: ${m('--spacing-m')};
  text-transform: uppercase;
`;

/* Standardize UI when rendered outside of MWP */
export const globalCss = css`
  ${normalizeCss}

  /* override site settings that override header behaviors */
  h1,h2,h3,h4,h5,h6 {
    font-family: ${m('--font-family')};
    line-height: inherit;
    text-transform: initial;
  }

  /* Ensure consistent rendering of lists */
  ul {
    list-style: disc outside;
  }
`;

const cookieModalHeaderCss = css`
  .modal-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: ${m('--spacing-xs')};

    .logo-container {
      display: flex;
      justify-content: center;
      flex-direction: column;
      opacity: ${m('--cookie-modal-logo-opacity')};
    }

    /* Ensure locale dropdown is aligned to the right regardless of logo display */
    .locale-container {
      margin-inline-start: auto;
    }
  }
`;

/* Shared rich text layout styles applied within both .contentful-rich-text and .category-description */
const richTextCss = css`
  a {
    color: inherit;
    font-weight: 500;
  }
  > ul {
    padding-left: ${m('--spacing-l')};
    margin-bottom: ${m('--spacing-s')};
  }
  > ul > li {
    margin-bottom: ${m('--spacing-xxs')};
  }
  > p {
    margin: 0 0 ${m('--spacing-xxs')};
  }
  *[dir='rtl'] & {
    > ul {
      padding-left: unset;
      padding-right: ${m('--spacing-l')};
    }
  }
`;

const cookieModalRichTextCss = css`
  .contentful-rich-text {
    /* Rich Text Base Styles */
    ${nonMobileMediaQuery} {
      font-size: ${m('--cookie-modal-rich-text-base-desktop-font-size')};
      font-weight: ${m('--cookie-modal-rich-text-base-desktop-font-weight')};
      line-height: ${m('--cookie-modal-rich-text-base-desktop-font-line-height')};
      font-stretch: ${m('--cookie-modal-rich-text-base-desktop-font-stretch')};
    }

    ${mobileMediaQuery} {
      font-size: ${m('--cookie-modal-rich-text-base-mobile-font-size')};
      font-weight: ${m('--cookie-modal-rich-text-base-mobile-font-weight')};
      line-height: ${m('--cookie-modal-rich-text-base-mobile-font-line-height')};
      font-stretch: ${m('--cookie-modal-rich-text-base-mobile-font-stretch')};
    }

    /* Rich Text H3 Styles (title) */
    h3 {
      ${nonMobileMediaQuery} {
        font-size: ${m('--cookie-modal-rich-text-title-desktop-font-size')};
        line-height: ${m('--cookie-modal-rich-text-title-desktop-font-line-height')};
        font-weight: ${m('--cookie-modal-rich-text-title-desktop-font-weight')};
        font-stretch: ${m('--cookie-modal-rich-text-title-desktop-font-stretch')};
      }
      ${mobileMediaQuery} {
        font-size: ${m('--cookie-modal-rich-text-title-mobile-font-size')};
        line-height: ${m('--cookie-modal-rich-text-title-mobile-font-line-height')};
        font-weight: ${m('--cookie-modal-rich-text-title-mobile-font-weight')};
        font-stretch: ${m('--cookie-modal-rich-text-title-mobile-font-stretch')};
      }
    }

    ${richTextCss}
  }
`;

/* Shared between cookieModalCss (modal settings screen) and cookieSettingsPageCss (standalone page) */
const cookieModalSettingsScreenCss = css`
  .category-border {
    border: ${m('--cookie-settings-category-border-width')} solid
      ${m('--cookie-settings-category-border-color')};
    border-radius: ${m('--cookie-settings-category-border-radius')};
    margin-bottom: ${m('--spacing-s')};
    padding: ${m('--cookie-settings-category-padding')};
  }

  .category-title-container {
    align-items: center;
    display: flex;
    margin-bottom: ${m('--spacing-xs')};
  }

  .category-title {
    flex: 1;

    ${nonMobileMediaQuery} {
      font-size: ${m('--cookie-settings-category-title-desktop-font-size')};
      font-stretch: ${m('--cookie-settings-category-title-desktop-font-stretch')};
      font-weight: ${m('--cookie-settings-category-title-desktop-font-weight')};
      line-height: ${m('--cookie-settings-category-title-desktop-font-line-height')};
    }
    ${mobileMediaQuery} {
      font-size: ${m('--cookie-settings-category-title-mobile-font-size')};
      font-stretch: ${m('--cookie-settings-category-title-mobile-font-stretch')};
      font-weight: ${m('--cookie-settings-category-title-mobile-font-weight')};
      line-height: ${m('--cookie-settings-category-title-mobile-font-line-height')};
    }

    *[dir='rtl'] & {
      text-align: right;
    }
  }

  .category-status {
    font-size: 14px;
    margin-right: ${m('--spacing-xs')};
    *[dir='rtl'] & {
      margin-right: unset;
      margin-left: ${m('--spacing-xs')};
    }
  }

  .category-toggle {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* Rich text base styles first; settings screen styles second for cascade priority */
  ${cookieModalRichTextCss}

  .category-description {
    ${nonMobileMediaQuery} {
      font-size: ${m('--cookie-settings-category-description-desktop-font-size')};
      font-stretch: ${m('--cookie-settings-category-description-desktop-font-stretch')};
      font-weight: ${m('--cookie-settings-category-description-desktop-font-weight')};
      line-height: ${m('--cookie-settings-category-description-desktop-font-line-height')};
    }
    ${mobileMediaQuery} {
      font-size: ${m('--cookie-settings-category-description-mobile-font-size')};
      font-stretch: ${m('--cookie-settings-category-description-mobile-font-stretch')};
      font-weight: ${m('--cookie-settings-category-description-mobile-font-weight')};
      line-height: ${m('--cookie-settings-category-description-mobile-font-line-height')};
    }
  }
`;

const cookieModalFooterCss = css`
  .modal-footer {
    padding-top: ${m('--spacing-s')}; /* Padding set by design */
    text-align: center;
    display: flex;
    /* Reverse direction so that when we wrap on smaller screens, the break happens on 1st, rather than last element.
       NOTE: requires that content be rendered in reverse order */
    flex-direction: row-reverse;
    gap: ${m('--spacing-s')};
    align-self: center;
    justify-content: center;

    /* Button spacing managed via gap instead of margins */
    .sdsm-button {
      margin: 0;
    }

    ${mobileMediaQuery} {
      flex-wrap: wrap;
      width: 100%;
    }

    ${smallMobileMediaQuery} {
      /* See note above, accounts for content rendered in reverse order. */
      flex-direction: column-reverse;
      align-items: center;
    }
  }
`;

const settingsPageFooterCss = css`
  .settings-page-footer {
    padding-top: ${m('--spacing-xl')};
    display: flex;
    flex-direction: row;
    gap: ${m('--spacing-s')};
    align-self: center;
    justify-content: center;

    /* Button spacing managed via gap instead of margins */
    .sdsm-button {
      margin: 0;
    }

    ${smallMobileMediaQuery} {
      padding-top: unset;
      flex-direction: column;
    }
  }
`;

export const contentCss = css`
  /* card style. */
  background-color: ${m('--bg-color')};
  border: ${m('--cookie-settings-border-width')} solid ${m('--cookie-modal-border-color')};
  border-radius: ${m('--cookie-modal-border-radius')};
  box-shadow: ${m('--cookie-settings-box-shadow')};
  color: ${m('--fg-color')};
  padding: ${m('--cookie-settings-padding')};
`;

export const cookieModalCss = css`
  --padding-horizontal: ${m('--cookie-modal-desktop-horizontal-padding')};
  --padding-vertical: ${m('--cookie-modal-desktop-vertical-padding')};

  ${mobileMediaQuery} {
    --padding-horizontal: ${m('--cookie-modal-mobile-horizontal-padding')};
    --padding-vertical: ${m('--cookie-modal-mobile-vertical-padding')};
  }

  --max-height: calc(100vh - var(--padding-vertical));
  /* stylelint-disable-next-line declaration-block-no-duplicate-custom-properties */
  --max-height: calc(100dvh - var(--padding-vertical));

  .sdsm-modal-content {
    ${contentCss}
    border-width: ${m('--cookie-modal-border-width')};
    box-shadow: ${m('--cookie-modal-box-shadow')};
    padding: var(--padding-vertical) var(--padding-horizontal);
    width: max-content;

    /* stop-gap solution to prevent any part from being hidden */
    overflow: auto;

    /* prevents overflow of this container. */
    max-height: 100%;
    max-width: 100%;

    ${nonMobileMediaQuery} {
      max-width: ${m('--cookie-settings-max-width')};
      min-width: ${m('--cookie-modal-min-width')};
    }
  }

  .cookie-landing-screen,
  .cookie-settings-screen {
    max-height: calc(var(--max-height) - calc(2 * calc(1px + var(--padding-vertical))));

    display: flex;
    flex-direction: column;

    /* Header Styles */
    ${cookieModalHeaderCss}

    /* Body Styles */
    .modal-body {
      flex: 1;
      overflow-y: auto;
      min-height: 5em;
      margin-bottom: ${m('--spacing-xs')};
      padding-right: ${m('--cookie-modal-body-inline-padding')};

      *[dir='rtl'] & {
        padding-right: unset;
        padding-left: ${m('--cookie-modal-body-inline-padding')};
      }

      .cookie-title {
        ${cookieTitleFontRampCss}
      }

      h3,
      b {
        font-weight: 500;
      }

      ${cookieModalSettingsScreenCss}
    }

    /* Footer Styles */
    ${cookieModalFooterCss}
  }
`;

export const cookieSettingsPageCss = css`
  /* Container Styles */
  justify-self: center;
  max-width: ${m('--cookie-settings-max-width')};

  /* Body Styles */
  .settings-page-body {
    text-align: left;

    .cookie-title {
      ${cookieTitleFontRampCss}
    }

    ${cookieModalSettingsScreenCss}
  }

  /* Footer Styles */
  ${settingsPageFooterCss}
`;
